import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Upload, X, CheckCircle, Info, FileText, Clock, ChevronDown } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Combobox, type ComboboxOption } from '@/components/ui/combobox'
import { getAreaTree, areaKeys } from '@/lib/api/areas'
import { listDocumentTypes, typeKeys } from '@/lib/api/document-types'
import { uploadDocumentWithProgress } from '@/lib/api/documents'
import { useUser } from '@/contexts/UserContext'
import { Permissions } from '@/lib/permissions'
import { validateFile, ALLOWED_EXTENSIONS } from '@/lib/uploadConstraints'
import { flattenUploadTargets } from '@/lib/areaTree'
import type { DocumentDetail } from '@/lib/types/ged'

type FileRow = {
  file: File; areaId: string; typeId: string; name: string; documentDate: string; description: string
}
type UploadResult = {
  row: FileRow
  result: DocumentDetail | null
  error: string | null
  loadedBytes: number
  status: 'queued' | 'uploading' | 'done' | 'error'
}
type Step = 1 | 2 | 3

const STEPS = [
  [1, 'Selecionar e Classificar'],
  [2, 'Envio'],
  [3, 'Concluído'],
] as const

function StepIndicator({ current }: { current: Step }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 22, alignItems: 'center' }}>
      {STEPS.map(([n, label], i) => {
        const done = n < current
        const active = n === current
        return (
          <div key={n} style={{ display: 'flex', alignItems: 'center', gap: i < STEPS.length - 1 ? 0 : undefined }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <div style={{ width: 24, height: 24, borderRadius: '50%',
                background: done ? 'var(--ev-positive)' : active ? 'var(--ev-accent)' : 'var(--bg-alt)',
                color: done || active ? 'white' : 'var(--fg-muted)',
                display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 600 }}>
                {done ? '✓' : n}
              </div>
              <span style={{ fontWeight: active ? 600 : 400, color: !active && !done ? 'var(--fg-muted)' : 'var(--fg)' }}>{label}</span>
            </div>
            {i < STEPS.length - 1 && <div style={{ width: 24, height: 1, background: done ? 'var(--ev-positive)' : 'var(--border)', margin: '0 4px' }} />}
          </div>
        )
      })}
    </div>
  )
}

type UploadWizardProps = {
  /** Pré-preenche a área de cada arquivo novo — usado quando o wizard abre a partir de uma pasta específica (popup). */
  initialAreaId?: string
  /** Quando fornecido, o passo 3 mostra um botão "Fechar" em vez de navegar para /admin/files. */
  onClose?: () => void
}

export function UploadWizard({ initialAreaId, onClose }: UploadWizardProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { hasPermission } = useUser()
  const canEdit = hasPermission(Permissions.DocumentManagementEdit)
  const [step, setStep] = useState<Step>(1)
  const [rows, setRows] = useState<FileRow[]>([])
  const [results, setResults] = useState<UploadResult[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [bulk, setBulk] = useState({ areaId: initialAreaId ?? '', typeId: '', documentDate: '', description: '' })
  const [bulkOpen, setBulkOpen] = useState(false)
  const [uploadMeta, setUploadMeta] = useState({ startTime: 0, endTime: 0, totalBytes: 0 })
  const [uploadedBytes, setUploadedBytes] = useState(0)
  const abortRef = useRef<AbortController | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: tree = [] } = useQuery({ queryKey: areaKeys.tree(), queryFn: getAreaTree, enabled: canEdit })
  const { data: types = [] } = useQuery({ queryKey: typeKeys.all(), queryFn: listDocumentTypes, enabled: canEdit && hasPermission(Permissions.DocumentTypesView) })

  const areas = flattenUploadTargets(tree)
  const areaOptions: ComboboxOption[] = areas.map((a) => ({ value: a.id, label: a.path }))

  const addFiles = (files: FileList | null) => {
    if (!files) return
    const accepted: FileRow[] = []
    const rejected: { name: string; reason: string }[] = []
    for (const f of Array.from(files)) {
      const check = validateFile(f)
      if (!check.ok) {
        rejected.push({ name: f.name, reason: check.reason })
        continue
      }
      accepted.push({
        file: f, areaId: initialAreaId ?? '', typeId: '', name: f.name.replace(/\.[^.]+$/, ''), documentDate: '', description: '',
      })
    }
    if (rejected.length > 0) {
      // Um toast por arquivo rejeitado — para 1, mostra a razão; para vários,
      // resume e detalha cada um em descrição.
      if (rejected.length === 1) {
        toast.error(`"${rejected[0]!.name}" recusado`, { description: rejected[0]!.reason })
      } else {
        toast.error(`${rejected.length} arquivos recusados`, {
          description: rejected.map((r) => `${r.name}: ${r.reason}`).join('\n'),
        })
      }
    }
    if (accepted.length > 0) setRows((r) => [...r, ...accepted])
  }

  const updateRow = (i: number, patch: Partial<FileRow>) => setRows((rs) => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r))
  const removeRow = (i: number) => setRows((rs) => rs.filter((_, idx) => idx !== i))
  const allReady = rows.length > 0 && rows.every((r) => r.areaId && r.typeId && r.name && r.documentDate)

  const applyBulk = () =>
    setRows((rs) =>
      rs.map((r) => ({
        ...r,
        areaId: bulk.areaId || r.areaId,
        typeId: bulk.typeId || r.typeId,
        documentDate: bulk.documentDate || r.documentDate,
        description: bulk.description || r.description,
      }))
    )

  const startUpload = async () => {
    const controller = new AbortController()
    abortRef.current = controller
    const totalBytes = rows.reduce((s, r) => s + r.file.size, 0)
    const startTime = Date.now()
    setUploadMeta({ startTime, endTime: 0, totalBytes })
    setUploadedBytes(0)
    setStep(2)

    const pending: UploadResult[] = rows.map((row) => ({
      row, result: null, error: null, loadedBytes: 0, status: 'queued',
    }))
    setResults([...pending])

    let completedBytes = 0
    for (let i = 0; i < rows.length; i++) {
      if (controller.signal.aborted) break
      const row = rows[i]!
      pending[i] = { ...pending[i]!, status: 'uploading' }
      setResults([...pending])

      try {
        const result = await uploadDocumentWithProgress(
          row,
          (loaded) => {
            pending[i] = { ...pending[i]!, loadedBytes: loaded }
            setResults([...pending])
            setUploadedBytes(completedBytes + loaded)
          },
          controller.signal,
        )
        completedBytes += row.file.size
        setUploadedBytes(completedBytes)
        pending[i] = { ...pending[i]!, result, loadedBytes: row.file.size, status: 'done' }
      } catch (e) {
        if ((e as DOMException).name === 'AbortError') break
        const message = e instanceof Error ? e.message : String(e)
        pending[i] = { ...pending[i]!, error: message, loadedBytes: 0, status: 'error' }
        toast.error(`Erro ao enviar ${row.name}`, { description: message })
      }
      setResults([...pending])
    }

    setUploadMeta((m) => ({ ...m, endTime: Date.now() }))
    setStep(3)

    // Pelo menos um arquivo foi enviado com sucesso — invalida os caches que
    // dependem do estado de documentos/áreas para que qualquer página aberta
    // (ex. AreaContentPage por trás do popup) refaça a busca e mostre os
    // arquivos novos e as contagens atualizadas, em vez de ficar com dados
    // obsoletos até a próxima navegação/remount.
    if (pending.some((r) => r.status === 'done')) {
      queryClient.invalidateQueries({ queryKey: ['documents'] })
      queryClient.invalidateQueries({ queryKey: ['areas'] })
    }
  }

  if (!canEdit) {
    return (
      <div style={{ padding: '24px 28px', maxWidth: 720 }}>
        <h1 style={{ fontSize: 24, marginBottom: 8 }}>Upload</h1>
        <p style={{ color: 'var(--fg-muted)', fontSize: 14 }}>
          Você não tem permissão para enviar arquivos.
        </p>
      </div>
    )
  }

  if (step === 1) return (
    <div style={{ padding: '24px 28px' }}>
      <h1 style={{ fontSize: 24, marginBottom: 4 }}>Upload</h1>
      <p style={{ color: 'var(--fg-muted)', fontSize: 13, marginBottom: 18 }}>Adicione múltiplos arquivos de uma vez.</p>
      <StepIndicator current={1} />

      <div className="card" style={{ padding: '10px 14px', marginBottom: 16,
        background: 'var(--accent-soft)', border: '1px solid var(--accent)',
        display: 'flex', alignItems: 'center', gap: 10, fontSize: 12 }}>
        <Info size={14} style={{ color: 'var(--accent)', flexShrink: 0 }} />
        <span>O envio dos arquivos só inicia após todos os metadados obrigatórios estarem preenchidos.</span>
      </div>

      <div
        className="card"
        style={{
          padding: 32,
          border: `2px dashed ${isDragOver ? 'var(--ev-positive)' : 'var(--ev-accent)'}`,
          background: isDragOver ? 'color-mix(in srgb, var(--ev-positive) 8%, transparent)' : 'var(--accent-soft)',
          textAlign: 'center',
          marginBottom: 18,
          transition: 'background 0.15s, border-color 0.15s',
        }}
        onDragEnter={(e) => { e.preventDefault(); setIsDragOver(true) }}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
        onDragLeave={(e) => { e.preventDefault(); setIsDragOver(false) }}
        onDrop={(e) => { e.preventDefault(); setIsDragOver(false); addFiles(e.dataTransfer.files) }}
      >
        <Upload size={36} style={{ color: isDragOver ? 'var(--ev-positive)' : 'var(--ev-accent)', marginBottom: 10 }} />
        <h3 style={{ fontSize: 16, marginBottom: 6 }}>
          {isDragOver ? 'Solte os arquivos aqui' : 'Arraste arquivos aqui ou clique para selecionar'}
        </h3>
        <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 12 }}>
          {ALLOWED_EXTENSIONS.map((e) => e.toUpperCase()).join(', ')} · até 50 MB por arquivo
        </p>
        {!isDragOver && (
          <>
            <Button variant="outline" type="button" onClick={() => fileInputRef.current?.click()}>
              Selecionar arquivos
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_EXTENSIONS.map((e) => `.${e}`).join(',')}
              style={{ display: 'none' }}
              onChange={(e) => addFiles(e.target.files)}
            />
          </>
        )}
      </div>

      {/* Card colapsável — fechado por default. Antes o card grande empurrava
          a lista de arquivos pra baixo da dobra, o usuário não via o arquivo
          recém-adicionado e re-incluía o mesmo arquivo várias vezes (relato
          do cliente). Trigger compacto mostra que o recurso existe sem
          ocupar espaço. */}
      <div className="card" style={{ marginBottom: 14, overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => setBulkOpen((o) => !o)}
          aria-expanded={bulkOpen}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            background: 'var(--bg-alt)',
            border: 0,
            cursor: 'pointer',
            textAlign: 'left',
            color: 'var(--fg)',
            fontFamily: 'inherit',
          }}
        >
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>Aplicar a todos os arquivos selecionados</span>
            <span style={{ fontSize: 11, color: 'var(--fg-muted)', fontWeight: 400 }}>
              Defina área, tipo, data ou descrição padrão pra todos os arquivos de uma vez (opcional).
            </span>
          </span>
          <ChevronDown
            size={16}
            style={{
              color: 'var(--fg-muted)',
              transition: 'transform 150ms',
              transform: bulkOpen ? 'rotate(180deg)' : 'none',
              flexShrink: 0,
            }}
          />
        </button>
        {bulkOpen && (
          <div style={{ padding: 16, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <Label style={{ fontSize: 11, marginBottom: 4, display: 'block', color: 'var(--fg-muted)' }}>Área</Label>
                <Combobox
                  options={areaOptions}
                  value={bulk.areaId}
                  onChange={(v) => setBulk((b) => ({ ...b, areaId: v }))}
                  placeholder="Selecione uma área…"
                  searchPlaceholder="Buscar por caminho…"
                  emptyText="Nenhuma área encontrada."
                  triggerHeight={36}
                />
              </div>
              <div>
                <Label style={{ fontSize: 11, marginBottom: 4, display: 'block', color: 'var(--fg-muted)' }}>Tipo</Label>
                <Select value={bulk.typeId} onValueChange={(v) => setBulk((b) => ({ ...b, typeId: v }))}>
                  <SelectTrigger style={{ height: 36, fontSize: 12 }}><SelectValue placeholder="Selecione um tipo…" /></SelectTrigger>
                  <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label style={{ fontSize: 11, marginBottom: 4, display: 'block', color: 'var(--fg-muted)' }}>Data padrão</Label>
                <Input type="date" value={bulk.documentDate}
                  onChange={(e) => setBulk((b) => ({ ...b, documentDate: e.target.value }))}
                  style={{ height: 36, fontSize: 12 }} />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Label style={{ fontSize: 11, marginBottom: 4, display: 'block', color: 'var(--fg-muted)' }}>Descrição</Label>
                <Textarea value={bulk.description} rows={2}
                  onChange={(e) => setBulk((b) => ({ ...b, description: e.target.value }))}
                  placeholder="Descrição que será aplicada a todos os arquivos selecionados"
                  style={{ fontSize: 12, resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <Button variant="outline" size="sm" onClick={applyBulk}>Aplicar a todos</Button>
            </div>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="card" style={{ overflow: 'hidden', marginBottom: 14 }}>
          <div style={{ padding: '12px 16px', background: 'var(--bg-alt)', borderBottom: '1px solid var(--border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h4 style={{ fontSize: 13 }}>{rows.length} arquivo(s) · {(rows.reduce((s, r) => s + r.file.size, 0) / 1024 / 1024).toFixed(1)} MB no total</h4>
              <p style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>Preencha os metadados de cada arquivo. Use "Aplicar a todos" acima para preencher em lote.</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setRows([])}>Limpar tudo</Button>
          </div>
          {/* Table header */}
          <div style={{ display: 'grid', gridTemplateColumns: '28px 1.4fr 1.6fr 110px 130px 1.4fr 28px', gap: 10,
            padding: '8px 16px', background: 'var(--bg-alt)', borderBottom: '1px solid var(--border)',
            fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--fg-muted)' }}>
            <div />
            <div>Área</div>
            <div>Nome do arquivo</div>
            <div>Data</div>
            <div>Tipo</div>
            <div>Descrição <span style={{ textTransform: 'none', fontWeight: 400, letterSpacing: 0 }}>(opcional)</span></div>
            <div />
          </div>
          {/* Rows */}
          {rows.map((row, i) => {
            const incomplete = !row.areaId || !row.typeId || !row.name || !row.documentDate
            return (
              <div key={i} style={{ padding: '12px 16px',
                borderBottom: i < rows.length - 1 ? '1px solid var(--divider)' : 'none',
                background: incomplete ? 'color-mix(in srgb, #e0a800 5%, transparent)' : 'transparent' }}>
                {/* Fields row */}
                <div style={{ display: 'grid', gridTemplateColumns: '28px 1.4fr 1.6fr 110px 130px 1.4fr 28px', gap: 10, alignItems: 'center' }}>
                  <FileText size={16} style={{ color: 'var(--fg-muted)' }} />
                  <Combobox
                    options={areaOptions}
                    value={row.areaId}
                    onChange={(v) => updateRow(i, { areaId: v })}
                    placeholder="Área…"
                    searchPlaceholder="Buscar por caminho…"
                    emptyText="Nenhuma área encontrada."
                    triggerHeight={32}
                  />
                  <Input value={row.name} onChange={(e) => updateRow(i, { name: e.target.value })}
                    style={{ height: 32, fontSize: 12 }} placeholder="Ex.: Ata 415 — Reunião Ordinária" />
                  <Input type="date" value={row.documentDate} onChange={(e) => updateRow(i, { documentDate: e.target.value })}
                    style={{ height: 32, fontSize: 12 }} />
                  <Select value={row.typeId} onValueChange={(v) => updateRow(i, { typeId: v })}>
                    <SelectTrigger style={{ height: 32, fontSize: 12 }}><SelectValue placeholder="Tipo…" /></SelectTrigger>
                    <SelectContent>{types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input value={row.description} onChange={(e) => updateRow(i, { description: e.target.value })}
                    style={{ height: 32, fontSize: 12 }} placeholder="Descrição opcional" />
                  <button onClick={() => removeRow(i)}
                    style={{ background: 'none', border: 0, padding: 4, color: 'var(--fg-muted)', cursor: 'pointer' }}>
                    <X size={13} />
                  </button>
                </div>
                {/* File meta + status row */}
                <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 10, alignItems: 'center', marginTop: 8 }}>
                  <div />
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 11, color: 'var(--fg-muted)', fontFamily: 'var(--font-mono)' }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.file.name}</span>
                    <span>·</span>
                    <span>{(row.file.size / 1024).toFixed(0)} KB</span>
                    {incomplete && (
                      <><span>·</span>
                      <span style={{ color: 'var(--ev-warning)', fontWeight: 600, fontFamily: 'var(--font-sans)' }}>
                        campos obrigatórios pendentes
                      </span></>
                    )}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 500, whiteSpace: 'nowrap',
                    color: incomplete ? 'var(--ev-warning)' : 'var(--ev-positive)' }}>
                    {incomplete ? 'aguarda preenchimento' : 'pronto'}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
          {rows.filter((r) => r.areaId && r.typeId && r.name && r.documentDate).length} prontos · {rows.filter((r) => !r.areaId || !r.typeId || !r.name || !r.documentDate).length} aguardando preenchimento
        </div>
        <Button disabled={!allReady} onClick={startUpload}>Iniciar envio →</Button>
      </div>
    </div>
  )

  if (step === 2) {
    const elapsedSec = Math.max(0.1, (Date.now() - uploadMeta.startTime) / 1000)
    const speedBps = uploadedBytes / elapsedSec
    const speedKBs = Math.round(speedBps / 1024)
    const remainingBytes = uploadMeta.totalBytes - uploadedBytes
    const etaSec = speedBps > 0 ? Math.ceil(remainingBytes / speedBps) : 0
    const overallPct = uploadMeta.totalBytes > 0
      ? Math.round((uploadedBytes / uploadMeta.totalBytes) * 100)
      : 0
    const uploadedMB = (uploadedBytes / 1024 / 1024).toFixed(1)
    const totalMB = (uploadMeta.totalBytes / 1024 / 1024).toFixed(1)
    const doneCount = results.filter((r) => r.status === 'done').length

    const statusColor: Record<string, string> = {
      done: 'var(--ev-positive)',
      uploading: 'var(--ev-accent)',
      queued: 'var(--fg-muted)',
      error: 'var(--ev-error)',
    }
    const statusLabel: Record<string, string> = {
      done: 'enviado', uploading: 'enviando', queued: 'fila', error: 'erro',
    }

    return (
      <div style={{ padding: '24px 28px', maxWidth: 980 }}>
        <h1 style={{ fontSize: 24, marginBottom: 4 }}>Enviando arquivos…</h1>
        <p style={{ color: 'var(--fg-muted)', fontSize: 13, marginBottom: 22 }}>
          Acompanhe o envio de cada arquivo. Não feche esta página até a conclusão.
        </p>
        <StepIndicator current={2} />

        {/* Overall progress */}
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Progresso geral</div>
              <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>
                {doneCount} de {results.length} arquivos enviados · {uploadedMB} MB de {totalMB} MB
              </div>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, fontFamily: 'var(--font-mono)', color: 'var(--ev-accent)' }}>
              {overallPct}%
            </div>
          </div>
          <Progress value={overallPct} style={{ height: 8 }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, fontSize: 11, color: 'var(--fg-muted)' }}>
            <span>Tempo estimado restante: {etaSec > 0 ? `${etaSec}s` : '—'}</span>
            <span>Velocidade: {speedKBs} KB/s</span>
          </div>
        </div>

        {/* Per-file list */}
        <div className="card" style={{ overflow: 'hidden', marginBottom: 16 }}>
          <div style={{ padding: '12px 16px', background: 'var(--bg-alt)', borderBottom: '1px solid var(--border)',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4 style={{ fontSize: 13 }}>Arquivos</h4>
            <Button variant="ghost" size="sm" disabled style={{ opacity: 0.5 }}>Pausar tudo</Button>
          </div>
          {results.map((r, i) => {
            const filePct = r.row.file.size > 0 ? Math.round((r.loadedBytes / r.row.file.size) * 100) : 0
            return (
              <div key={i} style={{ padding: '14px 16px',
                borderBottom: i < results.length - 1 ? '1px solid var(--divider)' : 'none' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '20px 1fr auto auto', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                  {r.status === 'done'
                    ? <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--ev-positive)',
                        color: 'white', display: 'grid', placeItems: 'center', fontSize: 11 }}>✓</div>
                    : r.status === 'uploading'
                      ? <Upload size={16} style={{ color: 'var(--ev-accent)' }} />
                      : <Clock size={16} style={{ color: 'var(--fg-muted)' }} />}
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500 }}>{r.row.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--fg-muted)', fontFamily: 'var(--font-mono)', marginTop: 1 }}>
                      {r.row.file.name} · {(r.row.file.size / 1024).toFixed(0)} KB
                    </div>
                  </div>
                  <div style={{ fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 600, minWidth: 48, textAlign: 'right',
                    color: statusColor[r.status] ?? 'var(--fg-muted)' }}>
                    {r.status === 'done' ? '100%' : r.status === 'uploading' ? `${filePct}%` : '0%'}
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 500, textTransform: 'capitalize', minWidth: 64, textAlign: 'right',
                    color: statusColor[r.status] ?? 'var(--fg-muted)' }}>
                    {statusLabel[r.status] ?? r.status}
                  </div>
                </div>
                <Progress
                  value={r.status === 'done' ? 100 : r.status === 'uploading' ? filePct : 0}
                  style={{ height: 4, marginLeft: 32,
                    '--progress-background': r.status === 'done' ? 'var(--ev-positive)' : 'var(--ev-accent)' } as React.CSSProperties}
                />
                {r.status === 'error' && r.error && (
                  <div style={{ marginLeft: 32, marginTop: 6, fontSize: 11, color: 'var(--ev-error)' }}>
                    {r.error}
                  </div>
                )}
              </div>
            )
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: 'var(--fg-muted)' }}>
            {doneCount} enviados · {results.filter((r) => r.status === 'uploading').length} enviando · {results.filter((r) => r.status === 'queued').length} na fila
          </div>
          <Button variant="outline" onClick={() => abortRef.current?.abort()}>Cancelar envio</Button>
        </div>
      </div>
    )
  }

  // Step 3 — Concluído
  const successResults = results.filter((r) => r.status === 'done')
  const errorResults = results.filter((r) => r.status === 'error')
  const totalSuccessSize = successResults.reduce((s, r) => s + r.row.file.size, 0)
  const affectedAreas = new Set(successResults.map((r) => r.row.areaId)).size
  const durationSec = uploadMeta.endTime > 0 ? Math.round((uploadMeta.endTime - uploadMeta.startTime) / 1000) : 0
  const durationLabel = durationSec >= 60
    ? `${Math.floor(durationSec / 60)}m ${durationSec % 60}s`
    : `${durationSec}s`

  return (
    <div style={{ padding: '24px 28px', maxWidth: 980 }}>
      <StepIndicator current={3} />

      {/* Success banner */}
      <div className="card" style={{ padding: 28, marginBottom: 18,
        background: 'rgba(45,157,110,0.08)', border: '1px solid var(--ev-positive)',
        display: 'flex', alignItems: 'center', gap: 18 }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'var(--ev-positive)',
          color: 'white', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <CheckCircle size={28} />
        </div>
        <div>
          <h2 style={{ fontSize: 20, marginBottom: 4 }}>
            {successResults.length} arquivo{successResults.length !== 1 ? 's' : ''} enviado{successResults.length !== 1 ? 's' : ''} com sucesso
          </h2>
          <p style={{ fontSize: 13, color: 'var(--fg-muted)' }}>
            Os arquivos já estão disponíveis no acervo e nos respectivos painéis de área.
          </p>
        </div>
      </div>

      {/* Stats cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 18 }}>
        {[
          { label: 'ARQUIVOS', value: String(successResults.length), color: 'var(--ev-accent)' },
          { label: 'TAMANHO TOTAL', value: `${(totalSuccessSize / 1024 / 1024).toFixed(1)} MB`, color: 'var(--ev-accent)' },
          { label: 'ÁREAS AFETADAS', value: String(affectedAreas), color: 'inherit' },
          { label: 'TEMPO DE ENVIO', value: durationLabel, color: 'inherit' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ padding: '16px 20px' }}>
            <div style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em',
              color: 'var(--fg-muted)', marginBottom: 8 }}>{label}</div>
            <div style={{ fontSize: 28, fontWeight: 700, fontFamily: 'var(--font-mono)', color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* File table */}
      <div className="card" style={{ overflow: 'hidden', marginBottom: 18 }}>
        <div style={{ padding: '12px 16px', background: 'var(--bg-alt)', borderBottom: '1px solid var(--border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 style={{ fontSize: 13 }}>Arquivos enviados</h4>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr 140px 80px 80px 80px 80px', gap: 10,
          padding: '8px 16px', background: 'var(--bg-alt)', borderBottom: '1px solid var(--border)',
          fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--fg-muted)' }}>
          <div /><div>NOME</div><div>ÁREA</div><div>TIPO</div><div>DATA</div><div>TAMANHO</div><div>AÇÕES</div>
        </div>
        {results.map((r, i) => (
          <div key={i} style={{
            borderBottom: i < results.length - 1 ? '1px solid var(--divider)' : 'none',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '24px 1fr 140px 80px 80px 80px 80px', gap: 10,
              padding: '12px 16px', alignItems: 'center', fontSize: 13 }}>
              <CheckCircle size={16} style={{ color: r.result ? 'var(--ev-positive)' : 'var(--ev-error)', flexShrink: 0 }} />
              <span style={{ fontWeight: 500 }}>{r.row.name}</span>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{r.result?.areaName ?? '—'}</span>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)' }}>{r.result?.typeName ?? '—'}</span>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)', fontFamily: 'var(--font-mono)' }}>
                {r.row.documentDate ? new Date(r.row.documentDate).toLocaleDateString('pt-BR') : '—'}
              </span>
              <span style={{ fontSize: 12, color: 'var(--fg-muted)', fontFamily: 'var(--font-mono)' }}>
                {(r.row.file.size / 1024).toFixed(0)} KB
              </span>
              {r.result
                ? <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/files/${r.result!.id}/edit`)}>✎ Editar</Button>
                : <span style={{ fontSize: 11, color: 'var(--ev-error)' }}>erro</span>}
            </div>
            {r.error && (
              <div style={{ padding: '0 16px 10px 50px', fontSize: 11, color: 'var(--ev-error)' }}>
                {r.error}
              </div>
            )}
          </div>
        ))}
      </div>

      {errorResults.length > 0 && (
        <div className="card" style={{ padding: '12px 16px', marginBottom: 18,
          background: 'rgba(220,53,69,0.06)', border: '1px solid var(--ev-error)',
          fontSize: 12, color: 'var(--ev-error)' }}>
          {errorResults.length} arquivo(s) falharam no envio. Tente novamente ou verifique a conexão.
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outline" onClick={() => { setRows([]); setResults([]); setStep(1) }}>
          <Upload size={14} style={{ marginRight: 6 }} /> Enviar mais arquivos
        </Button>
        {onClose ? (
          <Button onClick={onClose}>
            <CheckCircle size={14} style={{ marginRight: 6 }} /> Fechar
          </Button>
        ) : (
          <Button onClick={() => navigate('/admin/files')}>
            <CheckCircle size={14} style={{ marginRight: 6 }} /> Ir para a lista de arquivos →
          </Button>
        )}
      </div>
    </div>
  )
}
